import React, { useCallback, useMemo } from 'react';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { isInIframe } from '@shared/utils/isInIframe';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useNativeState } from '@shared/core/native';
import { selectMobileTopBarNavPages } from './selectMobileTopBarNavPages';
import { StickyNavVariant } from './StickyNavVariant';
import type { MobileTopBarNavProps } from './MobileTopBarNav.types';

export const MobileTopBarNav: React.FC<MobileTopBarNavProps> = ({ eventHandle, pages, layout }) => {
  const isMobile = useIsMobileScreen();
  const { getValueString } = useQueryParamHelper();
  // Admin preview (joy's own preview iframe) — distinct from other iframe contexts like Carbon
  // embedding or custom domains, where this is a real guest visit and the nav must stay fully
  // clickable. Bare isInIframe() would wrongly treat those as preview.
  // Detect via either ?preview=true or ctx=adminGuestSitePreview: the EventWorkspace dashboard
  // preview builds its iframe src with HTML-entity `&amp;` separators, which mangle the `preview`
  // key to `amp;preview`; the leading `ctx` param still parses, so it's the reliable signal there.
  const isAdminPreview = isInIframe() && (getValueString('preview') === 'true' || getValueString('ctx') === 'adminGuestSitePreview');
  // Skip in native webviews: the native app supplies its own chrome, and on the native
  // accommodations page the web header (and the EventMenu the hamburger opens) is intentionally
  // hidden — so the bar's hamburger would have nothing to open. This nav is mobile-web only.
  const { isNative } = useNativeState();
  const isEligible = isMobile && !isNative;
  const eventPageRoute = useEventPageRoute(eventHandle, pages);
  const telemetry = useGuestSiteTelemetry();

  const visiblePages = useMemo(() => selectMobileTopBarNavPages(pages, layout), [pages, layout]);

  const onLinkClick = useCallback(
    (pageSlug: string) => {
      // Don't pollute guest-usage dashboards with admin-preview interactions.
      if (isAdminPreview) return;
      telemetry.mobileTopBarNavLinkClicked(pageSlug);
    },
    [telemetry, isAdminPreview],
  );

  const onMenuClick = useCallback(() => {
    if (isAdminPreview) return;
    telemetry.mobileTopBarNavMenuOpened();
  }, [telemetry, isAdminPreview]);

  if (!isEligible) return null;
  if (visiblePages.length === 0) return null;

  return (
    <StickyNavVariant
      pages={visiblePages}
      onLinkClick={onLinkClick}
      onMenuClick={onMenuClick}
      eventPageRoute={eventPageRoute}
      isAdminPreview={isAdminPreview}
    />
  );
};

MobileTopBarNav.displayName = 'MobileTopBarNav';
